import apiClient, { handleApiResponse, handleApiError } from './apiClient';
import { 
    handleApiError as enhancedHandleApiError, 
    apiCallWithRetry,
    withRetry,
    retryOperation 
} from '../utils/errorHandler';

/**
 * Modern Patient Service
 * 
 * This service handles all patient-related API operations using modern REST endpoints.
 * It includes response transformation for legacy compatibility.
 */

class PatientService {
    constructor() {
        this.baseUrl = '/patients';
    }

    // ============================================================================
    // PATIENT CRUD OPERATIONS
    // ============================================================================

    /**
     * Get paginated list of patients
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Transformed patient list response
     */
    async getPatients(params = {}) {
        try {
            const response = await apiClient.get(this.baseUrl, { params });
            return handleApiResponse(response);
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Get single patient by ID
     * @param {string} patientId - Patient ID
     * @returns {Promise<Object>} Transformed patient response
     */
    async getPatientById(patientId) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/${patientId}`);
            return handleApiResponse(response);
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Create new patient pre-registration
     * @param {Object} patientData - Patient pre-registration data
     * @returns {Promise<Object>} Transformed patient response
     */
    async createPatientPreRegistration(patientData) {
        console.log('🏥 PATIENT SERVICE: createPatientPreRegistration called');
        console.log('📥 Input patient data:', patientData);
        
        try {
            console.log('🔍 Checking if patientData.demographics exists...');
            if (patientData.demographics) {
                console.log('✅ Demographics data found:', patientData.demographics);
            } else {
                console.warn('⚠️ No demographics data in patientData');
            }
            
            console.log('🌐 Making API call to:', `${this.baseUrl}/pre-register`);
            console.log('📤 Request payload:', patientData);
            
            const response = await apiClient.post(`${this.baseUrl}/pre-register`, patientData);
            
            console.log('✅ API call successful');
            console.log('📥 Raw API response:', response);
            console.log('📥 Response data:', response.data);
            
            return response.data;
        } catch (error) {
            console.error('💥 ERROR in createPatientPreRegistration:', error.message);
            
            throw handleApiError(error);
        }
    }

    /**
     * Complete patient registration
     * @param {Object} registrationData - Patient registration completion data
     * @returns {Promise<Object>} Transformed patient response
     */
    async completePatientRegistration(registrationData) {
        return apiCallWithRetry(
            async () => {
                // Validate registration data before sending
                this.validateRegistrationData(registrationData);
                
                const response = await apiClient.post(`${this.baseUrl}/register`, registrationData);
                
                // Validate response structure
                if (!response.data) {
                    throw new Error('Invalid response format from server');
                }
                
                return response.data;
            },
            {
                context: 'Patient Registration',
                showLoading: true,
                showError: true,
                retryOptions: {
                    maxRetries: 2, // Registration should only retry once
                    initialDelay: 2000,
                    retryCondition: (error, attempt) => {
                        // Only retry on network or server errors, not validation errors
                        const status = error?.response?.status;
                        return status >= 500 || !status; // Server errors or network issues
                    }
                },
                loadingCallback: (message) => window.toast?.loading(message),
                errorCallback: (errorResult) => {
                    console.error('Registration failed:', errorResult);
                    
                    // Provide specific guidance for registration errors
                    if (errorResult.details?.code === 'VALIDATION_ERROR') {
                        window.toast?.error('Please check your input data and try again.');
                    } else if (errorResult.details?.code === 'PATIENT_MRN_ERROR') {
                        window.toast?.error('There was an issue with the Medical Record Number. Please try again.');
                    } else if (errorResult.details?.code === 'PATIENT_REGISTRATION_ERROR') {
                        window.toast?.error('Registration failed. Please verify all required information is provided.');
                    }
                }
            }
        );
    }

    /**
     * Validate registration data before submission
     * @param {Object} data - Registration data to validate
     * @throws {Error} If validation fails
     */
    validateRegistrationData(data) {
        if (!data) {
            throw new Error('Registration data is required');
        }
        
        // Check required fields
        const requiredFields = ['first_name', 'last_name'];
        const missingFields = requiredFields.filter(field => !data[field] || !data[field].trim());
        
        if (missingFields.length > 0) {
            throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }
        
        // Validate Emirates ID format if provided (optional)
        if (data.emirates_id) {
            const cleanId = data.emirates_id.replace(/[-\s]/g, '');
            if (!/^784\d{12}$/.test(cleanId)) {
                throw new Error('Invalid Emirates ID format. Must be 15 digits starting with 784.');
            }
        }
        
        // Validate email format if provided
        if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
            throw new Error('Invalid email format');
        }
        
        // Validate phone format if provided
        if (data.phone_primary && !/^\+?[1-9]\d{1,14}$/.test(data.phone_primary)) {
            throw new Error('Invalid phone number format');
        }
    }

    /**
     * Update patient information
     * @param {string} patientId - Patient ID
     * @param {Object} patientData - Patient update data
     * @returns {Promise<Object>} Transformed patient response
     */
    async updatePatient(patientId, patientData) {
        try {
            const response = await apiClient.put(`${this.baseUrl}/${patientId}`, patientData);
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Delete patient (soft delete)
     * @param {string} patientId - Patient ID
     * @returns {Promise<Object>} Success response
     */
    async deletePatient(patientId) {
        try {
            await apiClient.delete(`${this.baseUrl}/${patientId}`);
            return { success: true, message: "Patient deleted successfully" };
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Bulk delete patients
     * @param {Array<string>} patientIds - Array of patient IDs
     * @returns {Promise<Object>} Success response
     */
    async bulkDeletePatients(patientIds) {
        try {
            const response = await apiClient.delete(`${this.baseUrl}/bulk`, {
                data: { patient_ids: patientIds }
            });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // PATIENT STATUS MANAGEMENT
    // ============================================================================

    /**
     * Activate patient
     * @param {string} patientId - Patient ID
     * @returns {Promise<Object>} Success response
     */
    async activatePatient(patientId) {
        try {
            const response = await apiClient.patch(`${this.baseUrl}/${patientId}/activate`);
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Deactivate patient
     * @param {string} patientId - Patient ID
     * @returns {Promise<Object>} Success response
     */
    async deactivatePatient(patientId) {
        try {
            const response = await apiClient.patch(`${this.baseUrl}/${patientId}/deactivate`);
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // PATIENT SEARCH & FILTERING
    // ============================================================================

    /**
     * Search patients with advanced filters
     * @param {Object} searchParams - Search parameters
     * @returns {Promise<Object>} Transformed patient list response
     */
    async searchPatients(searchParams) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/search`, { params: searchParams });
            return handleApiResponse(response);
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Search patients by MRN
     * @param {string} mrn - Medical Record Number
     * @returns {Promise<Object>} Transformed patient response
     */
    async searchPatientByMRN(mrn) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/mrn/${mrn}`);
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Search patients by EMR number
     * @param {string} emrNumber - EMR Number
     * @returns {Promise<Object>} Transformed patient response
     */
    async searchPatientByEMR(emrNumber) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/search`, {
                params: { emr_number: emrNumber }
            });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // PATIENT MEDICAL RECORDS
    // ============================================================================

    /**
     * Get patient medical history
     * @param {string} patientId - Patient ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Medical history response
     */
    async getPatientMedicalHistory(patientId, params = {}) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/${patientId}/medical-history`, { params });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Add medical record entry
     * @param {string} patientId - Patient ID
     * @param {Object} recordData - Medical record data
     * @returns {Promise<Object>} Success response
     */
    async addMedicalRecord(patientId, recordData) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/${patientId}/medical-records`, recordData);
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Update medical record entry
     * @param {string} patientId - Patient ID
     * @param {string} recordId - Record ID
     * @param {Object} recordData - Updated record data
     * @returns {Promise<Object>} Success response
     */
    async updateMedicalRecord(patientId, recordId, recordData) {
        try {
            const response = await apiClient.put(`${this.baseUrl}/${patientId}/medical-records/${recordId}`, recordData);
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // PATIENT APPOINTMENTS
    // ============================================================================

    /**
     * Get patient appointments
     * @param {string} patientId - Patient ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Appointments response
     */
    async getPatientAppointments(patientId, params = {}) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/${patientId}/appointments`, { params });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Schedule appointment for patient
     * @param {string} patientId - Patient ID
     * @param {Object} appointmentData - Appointment data
     * @returns {Promise<Object>} Success response
     */
    async scheduleAppointment(patientId, appointmentData) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/${patientId}/appointments`, appointmentData);
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // PATIENT MEDICATIONS
    // ============================================================================

    /**
     * Get patient medications
     * @param {string} patientId - Patient ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Medications response
     */
    async getPatientMedications(patientId, params = {}) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/${patientId}/medications`, { params });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Add medication to patient
     * @param {string} patientId - Patient ID
     * @param {Object} medicationData - Medication data
     * @returns {Promise<Object>} Success response
     */
    async addPatientMedication(patientId, medicationData) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/${patientId}/medications`, medicationData);
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }


    // ============================================================================
    // PATIENT DOCUMENTS
    // ============================================================================

    /**
     * Get patient documents
     * @param {string} patientId - Patient ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Documents response
     */
    async getPatientDocuments(patientId, params = {}) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/${patientId}/documents`, { params });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Upload document for patient
     * @param {string} patientId - Patient ID
     * @param {FormData} documentData - Document form data
     * @returns {Promise<Object>} Success response
     */
    async uploadPatientDocument(patientId, documentData) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/${patientId}/documents`, documentData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // PATIENT STATISTICS
    // ============================================================================

    /**
     * Get patient statistics
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Statistics response
     */
    async getPatientStatistics(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/statistics`, { params });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // LEGACY COMPATIBILITY METHODS
    // ============================================================================

    /**
     * Legacy method: Get patient list
     * @param {Object} params - Legacy parameters
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getPatientListAction(params = {}) {
        return this.getPatients(params);
    }

    /**
     * Legacy method: Save patient (create or update)
     * @param {Object} patientData - Patient data
     * @returns {Promise<Object>} Legacy formatted response
     */
    async savePatientAction(patientData) {
        if (patientData.id) {
            return this.updatePatient(patientData.id, patientData);
        } else {
            return this.createPatient(patientData);
        }
    }

    /**
     * Legacy method: Delete patient
     * @param {string} patientId - Patient ID
     * @returns {Promise<Object>} Legacy formatted response
     */
    async deletePatientAction(patientId) {
        return this.deletePatient(patientId);
    }

    /**
     * Legacy method: Get patient details
     * @param {string} patientId - Patient ID
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getPatientDetailsAction(patientId) {
        return this.getPatientById(patientId);
    }

    /**
     * Legacy method: Search patient by MRN
     * @param {string} mrn - Medical Record Number
     * @returns {Promise<Object>} Legacy formatted response
     */
    async searchPatientByMRNAction(mrn) {
        return this.searchPatientByMRN(mrn);
    }

    /**
     * Legacy method: Get patient credentials (for authentication)
     * @param {Object} params - Search parameters
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getPatientCredentialsAction(params = {}) {
        return this.searchPatients(params);
    }

    /**
     * Legacy method: Add patient credentials
     * @param {Object} credentialsData - Credentials data
     * @returns {Promise<Object>} Legacy formatted response
     */
    async addPatientCredentialsAction(credentialsData) {
        return this.createPatient(credentialsData);
    }

    /**
     * Legacy method: Update patient credentials
     * @param {string} patientId - Patient ID
     * @param {Object} credentialsData - Updated credentials data
     * @returns {Promise<Object>} Legacy formatted response
     */
    async updatePatientCredentialsAction(patientId, credentialsData) {
        return this.updatePatient(patientId, credentialsData);
    }

    /**
     * Legacy method: Get patient medical history
     * @param {string} patientId - Patient ID
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getPatientMedicalHistoryAction(patientId) {
        return this.getPatientMedicalHistory(patientId);
    }

    /**
     * Transform legacy patient data format to modern API format
     * @param {Object} legacyData - Legacy patient data
     * @returns {Object} Modern patient data format
     */
    transformLegacyToModern(legacyData) {
        // Extract name from userName field
        const fullName = legacyData.userName || '';
        const nameParts = fullName.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        return {
            demographics: {
                first_name: firstName,
                last_name: lastName,
                middle_name: '',
                date_of_birth: new Date().toISOString(), // Default to today if not provided
                gender: 'unknown',
                nationality: 'UAE',
                phone_primary: '',
                phone_secondary: '',
                email: '',
                street: '',
                city: '',
                emirate: '',
                country: 'UAE',
                postal_code: '',
                emergency_contact_name: '',
                emergency_contact_phone: '',
                emergency_contact_relationship: ''
            },
            emirates_id: legacyData.MRNno || null,
            passport_number: null,
            planned_services: []
        };
    }
}

// Export singleton instance
export default new PatientService();